import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus, Search, Tags, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ExportButton } from "@/components/ExportButton";
import { ProductCard } from "@/components/catalog/ProductCard";
import { CategoriesDialog } from "@/components/catalog/CategoriesDialog";
import { ImportDialog } from "@/components/catalog/ImportDialog";
import { Button } from "@/components/ui/button";
import { Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { Checkbox, Input, Select } from "@/components/ui/form";
import { Pagination } from "@/components/ui/pagination";
import { api, unwrap } from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth";
import { catalogQuery, QUALITY_KEYS } from "@/lib/catalog";
import { useDebounced } from "@/lib/hooks";
import { useT } from "@/lib/i18n";

const PAGE_SIZE = 12;

/** Katalog: mijoz (`/my/catalog`) ham, xodim (`/catalog`) ham shu komponentdan foydalanadi. */
export function CatalogPage({ mode }: { mode: "staff" | "customer" }) {
  const { t } = useT();
  const { user } = useAuth();
  const staff = mode === "staff";
  const base = staff ? "/catalog" : "/my/catalog";
  const [sp, setSp] = useSearchParams();
  const [text, setText] = useState(sp.get("q") ?? "");
  const debounced = useDebounced(text);
  const [importing, setImporting] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries({ page: "", ...patch })) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSp(next, { replace: true });
  }
  useEffect(() => {
    if (debounced !== (sp.get("q") ?? "")) update({ q: debounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const query = catalogQuery(sp, PAGE_SIZE, staff);
  const list = useQuery({
    queryKey: ["catalog", mode, query],
    queryFn: async () => unwrap(await api.GET("/api/products/", { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const categories = useQuery({
    queryKey: ["categories"],
    queryFn: async () => unwrap(await api.GET("/api/categories/")),
  });
  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: async () => unwrap(await api.GET("/api/products/brands/")),
  });
  const rows = list.data?.results ?? [];

  return (
    <>
      <PageHeader
        title={t("nav.catalog")}
        actions={
          staff && (
            <>
              {isAdmin(user) && (
                <Button variant="outline" onClick={() => setCategoriesOpen(true)}>
                  <Tags className="size-4" /> {t("catalog.categories")}
                </Button>
              )}
              <Button variant="outline" onClick={() => setImporting(true)}>
                <Upload className="size-4" /> {t("catalog.import")}
              </Button>
              <ExportButton kind="products" />
              <Button asChild>
                <Link to="/catalog/new">
                  <Plus className="size-4" /> {t("catalog.new")}
                </Link>
              </Button>
            </>
          )
        }
      />

      <Card className="mb-4 grid gap-3 md:grid-cols-4">
        <div className="relative md:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-3.5 size-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("catalog.search")}
            aria-label={t("catalog.search")}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <Select
          aria-label={t("catalog.category")}
          value={sp.get("category") ?? ""}
          onChange={(e) => update({ category: e.target.value })}
        >
          <option value="">{t("catalog.all_categories")}</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t("catalog.brand")}
          value={sp.get("brand") ?? ""}
          onChange={(e) => update({ brand: e.target.value })}
        >
          <option value="">{t("catalog.all_brands")}</option>
          {brands.data?.brands.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t("catalog.quality")}
          value={sp.get("quality") ?? ""}
          onChange={(e) => update({ quality: e.target.value })}
        >
          <option value="">{t("catalog.all_qualities")}</option>
          {(Object.keys(QUALITY_KEYS) as (keyof typeof QUALITY_KEYS)[]).map((q) => (
            <option key={q} value={q}>
              {t(QUALITY_KEYS[q])}
            </option>
          ))}
        </Select>
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          placeholder={t("catalog.price_from")}
          aria-label={t("catalog.price_from")}
          value={sp.get("price_min") ?? ""}
          onChange={(e) => update({ price_min: e.target.value })}
        />
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          placeholder={t("catalog.price_to")}
          aria-label={t("catalog.price_to")}
          value={sp.get("price_max") ?? ""}
          onChange={(e) => update({ price_max: e.target.value })}
        />
        <Select
          aria-label={t("props.sort")}
          value={sp.get("ordering") ?? ""}
          onChange={(e) => update({ ordering: e.target.value })}
        >
          <option value="">{t("catalog.sort_new")}</option>
          <option value="price">{t("catalog.sort_price_asc")}</option>
          <option value="-price">{t("catalog.sort_price_desc")}</option>
          <option value="name">{t("catalog.sort_name")}</option>
          <option value="popular">{t("catalog.sort_popular")}</option>
        </Select>
        <div className="flex flex-wrap items-center gap-x-6 md:col-span-4">
          <Checkbox
            label={t("catalog.only_in_stock")}
            checked={sp.get("in_stock") === "true"}
            onChange={(e) => update({ in_stock: e.target.checked ? "true" : "" })}
          />
          {staff && (
            <Checkbox
              label={t("catalog.only_mine")}
              checked={sp.get("mine") === "1"}
              onChange={(e) => update({ mine: e.target.checked ? "1" : "" })}
            />
          )}
        </div>
      </Card>

      {list.isLoading && <Spinner className="m-8 size-8" />}
      {list.data && rows.length === 0 && <EmptyState>{t("catalog.empty")}</EmptyState>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {rows.map((p) => (
          <ProductCard key={p.id} product={p} to={`${base}/${p.id}`} showStats={staff} />
        ))}
      </div>
      <Pagination
        page={query.page}
        count={list.data?.count ?? 0}
        pageSize={PAGE_SIZE}
        onPage={(n) => update({ page: String(n) })}
      />

      {staff && <ImportDialog open={importing} onOpenChange={setImporting} />}
      {staff && isAdmin(user) && (
        <CategoriesDialog open={categoriesOpen} onOpenChange={setCategoriesOpen} />
      )}
    </>
  );
}
